import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type EditableRow = {
  clientId: string;
  id: number;
};

type RowTransform<T extends EditableRow> = (rows: T[], row: T) => T[];

type EditableRowListOptions<T extends EditableRow> = {
  areRowsEqual: (first: T[], second: T[]) => boolean;
  createEmptyRow: (temporaryId: number, isInitial: boolean) => T;
  defaultEditing?: boolean;
  isLoading?: boolean;
  mergeServerRowsWhileEditing?: boolean;
  serverRows: T[];
};

type EditableRowListState<T extends EditableRow, E> = {
  addRow: (transform?: RowTransform<T>) => T;
  cancelEditing: () => void;
  changeRow: (row: T, transform?: RowTransform<T>) => void;
  clearValidationErrors: () => void;
  completeSave: () => void;
  deleteRow: (row: T, transform?: RowTransform<T>) => void;
  deletedRows: T[];
  editingRowIds: Set<string>;
  enterEditing: () => void;
  hasChanges: boolean;
  isEditing: boolean;
  openRows: (clientIds: Iterable<string>) => void;
  patchRow: (clientId: string, changes: Partial<T>) => void;
  rows: T[];
  setEditingRowIds: Dispatch<SetStateAction<Set<string>>>;
  setRows: Dispatch<SetStateAction<T[]>>;
  setValidationErrors: Dispatch<SetStateAction<Map<string, E>>>;
  validationErrors: Map<string, E>;
  viewModeSignal: number | undefined;
};

export function useEditableRowList<T extends EditableRow, E>({
  areRowsEqual,
  createEmptyRow,
  defaultEditing = false,
  isLoading = false,
  mergeServerRowsWhileEditing = false,
  serverRows,
}: EditableRowListOptions<T>): EditableRowListState<T, E> {
  const [rows, setRows] = useState<T[]>([]);
  const [editingRowIds, setEditingRowIds] = useState<Set<string>>(new Set());
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const [deletedRowIds, setDeletedRowIds] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Map<string, E>>(
    new Map(),
  );
  const [viewModeSignal, setViewModeSignal] = useState<number | undefined>();
  const nextTemporaryId = useRef(-1);

  const initializeEditing = useCallback(() => {
    const initialRow = createEmptyRow(-1, true);
    nextTemporaryId.current = -2;
    setRows([initialRow, ...serverRows.map((row) => ({ ...row }))]);
    setEditingRowIds(new Set([initialRow.clientId]));
    setDeletedRowIds(new Set());
    setValidationErrors(new Map());
  }, [createEmptyRow, serverRows]);

  useEffect(() => {
    if (defaultEditing && isEditing && !isLoading && rows.length === 0) {
      initializeEditing();
    }
  }, [defaultEditing, initializeEditing, isEditing, isLoading, rows.length]);

  useEffect(() => {
    if (!mergeServerRowsWhileEditing || !isEditing) {
      return;
    }
    setRows((current) => {
      const currentIds = new Set(current.map((row) => row.clientId));
      const additions = serverRows.filter(
        (row) =>
          !currentIds.has(row.clientId) && !deletedRowIds.has(row.clientId),
      );
      return additions.length ? [...current, ...additions] : current;
    });
  }, [deletedRowIds, isEditing, mergeServerRowsWhileEditing, serverRows]);

  const reset = useCallback(() => {
    nextTemporaryId.current = -1;
    setRows([]);
    setEditingRowIds(new Set());
    setDeletedRowIds(new Set());
    setValidationErrors(new Map());
    setIsEditing(false);
  }, []);

  const enterEditing = useCallback(() => {
    setIsEditing(true);
    initializeEditing();
  }, [initializeEditing]);

  const addRow = useCallback(
    (transform?: RowTransform<T>) => {
      const row = createEmptyRow(nextTemporaryId.current, false);
      nextTemporaryId.current -= 1;
      setRows((current) =>
        transform ? transform(current, row) : [row, ...current],
      );
      setEditingRowIds((current) => new Set(current).add(row.clientId));
      setValidationErrors(new Map());
      return row;
    },
    [createEmptyRow],
  );

  const changeRow = useCallback((row: T, transform?: RowTransform<T>) => {
    setRows((current) =>
      transform
        ? transform(current, row)
        : current.map((item) => (item.clientId === row.clientId ? row : item)),
    );
    setValidationErrors(new Map());
  }, []);

  const deleteRow = useCallback((row: T, transform?: RowTransform<T>) => {
    if (row.id > 0) {
      setDeletedRowIds((current) => new Set(current).add(row.clientId));
    }
    setRows((current) =>
      transform
        ? transform(current, row)
        : current.filter((item) => item.clientId !== row.clientId),
    );
    setEditingRowIds((current) => {
      const next = new Set(current);
      next.delete(row.clientId);
      return next;
    });
    setValidationErrors(new Map());
  }, []);

  const patchRow = useCallback((clientId: string, changes: Partial<T>) => {
    setRows((current) =>
      current.map((row) =>
        row.clientId === clientId ? { ...row, ...changes } : row,
      ),
    );
    setValidationErrors(new Map());
  }, []);

  const openRows = useCallback((clientIds: Iterable<string>) => {
    setEditingRowIds(
      (current) => new Set([...current, ...Array.from(clientIds)]),
    );
  }, []);

  const completeSave = useCallback(() => {
    reset();
    setViewModeSignal((value = 0) => value + 1);
  }, [reset]);

  const deletedRows = useMemo(
    () => serverRows.filter((row) => deletedRowIds.has(row.clientId)),
    [deletedRowIds, serverRows],
  );
  const hasChanges =
    isEditing && (deletedRowIds.size > 0 || !areRowsEqual(rows, serverRows));

  return {
    addRow,
    cancelEditing: reset,
    changeRow,
    clearValidationErrors: () => setValidationErrors(new Map()),
    completeSave,
    deleteRow,
    deletedRows,
    editingRowIds,
    enterEditing,
    hasChanges,
    isEditing,
    openRows,
    patchRow,
    rows,
    setEditingRowIds,
    setRows,
    setValidationErrors,
    validationErrors,
    viewModeSignal,
  };
}

// Rows are validated as soon as they change, like regular form fields. A new
// row is left alone while it is in its initial state - untouched, or edited
// back to it - because then it is only the unused "add a row" slot.
export function getLiveRowErrors<T extends EditableRow, E>(
  rows: T[],
  savedErrors: Map<string, E>,
  validateRow: (row: T) => E,
  isInitialNewRow: (row: T) => boolean,
) {
  const errors = new Map(savedErrors);
  rows.forEach((row) => {
    if (isInitialNewRow(row)) {
      errors.delete(row.clientId);
      return;
    }
    // Keeps cross-row errors (e.g. duplicates) found by the last save.
    errors.set(row.clientId, {
      ...savedErrors.get(row.clientId),
      ...validateRow(row),
    });
  });
  return errors;
}

export function useLiveRowErrors<T extends EditableRow, E>(
  rows: T[],
  savedErrors: Map<string, E>,
  validateRow: (row: T) => E,
  isInitialNewRow: (row: T) => boolean,
) {
  return useMemo(
    () => getLiveRowErrors(rows, savedErrors, validateRow, isInitialNewRow),
    [isInitialNewRow, rows, savedErrors, validateRow],
  );
}

type CreateEditableRowDeltaOptions<T extends EditableRow, C, U, D> = {
  deletedRows: T[];
  includeMissingServerRowsAsDeleted?: boolean;
  isSameRow: (working: T, server: T) => boolean;
  serverRows: T[];
  toCreated: (row: T) => C;
  toDeleted: (row: T) => D;
  toUpdated: (row: T) => U;
  workingRows: T[];
};

export function createEditableRowDelta<T extends EditableRow, C, U, D>({
  deletedRows,
  includeMissingServerRowsAsDeleted = false,
  isSameRow,
  serverRows,
  toCreated,
  toDeleted,
  toUpdated,
  workingRows,
}: CreateEditableRowDeltaOptions<T, C, U, D>) {
  const serverById = new Map(serverRows.map((row) => [row.id, row]));
  const workingIds = new Set(
    workingRows.filter((row) => row.id > 0).map((row) => row.id),
  );
  const deletedById = new Map(deletedRows.map((row) => [row.id, row]));
  if (includeMissingServerRowsAsDeleted) {
    serverRows.forEach((row) => {
      if (!workingIds.has(row.id)) {
        deletedById.set(row.id, row);
      }
    });
  }
  const created = workingRows.filter((row) => row.id < 0).map(toCreated);
  const updated = workingRows
    .filter((row) => {
      const serverRow = serverById.get(row.id);
      return row.id > 0 && serverRow && !isSameRow(row, serverRow);
    })
    .map(toUpdated);
  return {
    created,
    updated,
    deleted: [...deletedById.values()].map(toDeleted),
  };
}
